// AppFooter.jsx
import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Calendar,
  Users,
  FileText,
  User as UserIcon,
  Settings,
  Plus,
} from "lucide-react";

const AppFooter = ({
  categories,
  elevation = true,
  showFab = false, // ignored; upload is a tab now
  uploadTo,
  onUploadClick,
  onUploadFiles,
  uploadAccept = "image/*,video/*",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!categories) return;
    const routeItem = categories.find(
      (i) => i.to && location.pathname.startsWith(i.to)
    );
    if (routeItem) setActiveKey(routeItem.key);
  }, [location.pathname, categories]);

  const defaultItems = [
    { key: "home",     label: "Home",     anchor: "top",      Icon: Home },
    { key: "today",    label: "Today",    anchor: "today",    Icon: Calendar },
    { key: "patients", label: "Patients", anchor: "bookings", Icon: Users },
    { key: "research", label: "Research", anchor: "research", Icon: FileText },
    { key: "upload",   label: "Upload",   action: "upload",   Icon: Plus }, // upload as normal tab
    { key: "profile",  label: "Profile",  anchor: "header",   Icon: UserIcon },
    // swap one above to keep Settings if needed:
    // { key: "settings", label: "Settings", to: "/settings", Icon: Settings },
  ];

  const items = React.useMemo(() => {
    if (!categories || !Array.isArray(categories) || !categories.length)
      return defaultItems;

    const hasUpload = categories.some((i) => i.key === "upload");
    if (hasUpload) return categories;

    // Add upload if missing; keep order at 6 items for the same look
    return [
      ...categories.slice(0, 5),
      { key: "upload", label: "Upload", action: "upload", Icon: Plus },
      ...categories.slice(5),
    ].slice(0, 6);
  }, [categories]);

  const isRouteActive = (to) => {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const handleUploadAction = () => {
    if (typeof onUploadClick === "function") return onUploadClick();
    if (typeof onUploadFiles === "function" && fileInputRef.current)
      return fileInputRef.current.click();
    if (uploadTo) return navigate(uploadTo);
  };

  const handleItemClick = (item) => {
    if (item.action === "upload") {
      handleUploadAction();
      setActiveKey(item.key);
      return;
    }
    if (typeof item.action === "function") {
      item.action();
      setActiveKey(item.key);
      return;
    }
    if (item.anchor) {
      if (item.anchor === "top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveKey(item.key);
        return;
      }
      const el = document.getElementById(item.anchor);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setActiveKey(item.key);
        return;
      }
      if (item.to) navigate(item.to);
      return;
    }
    if (item.to) {
      navigate(item.to);
      setActiveKey(item.key);
    }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length && typeof onUploadFiles === "function")
      onUploadFiles(files);
    e.target.value = "";
  };

  // one-row footer height
  const spacerHeight = 72;

  return (
    <>
      <div aria-hidden="true" style={{ height: spacerHeight }} />

      <nav
        role="navigation"
        aria-label="App footer"
        style={styles.nav(elevation)}
      >
        <style>{css}</style>

        <div className="footer-wrap">
          {/* hidden input for Upload tab */}
          <input
            ref={fileInputRef}
            type="file"
            accept={uploadAccept}
            multiple
            style={{ display: "none" }}
            onChange={onPickFiles}
          />

          {/* ALWAYS one row: grid columns = item count */}
          <ul
            className="footer-grid"
            role="list"
            style={{
              gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
            }}
          >
            {items.map(({ key, label, to, Icon, anchor, action, badge }) => {
              const routeActive = isRouteActive(to);
              const active = routeActive || activeKey === key;

              return (
                <li key={key}>
                  <button
                    className={`footer-btn ${
                      active ? "footer-btn--active" : ""
                    }`}
                    aria-label={label}
                    aria-current={active ? "page" : undefined}
                    onClick={() =>
                      handleItemClick({
                        key,
                        label,
                        to,
                        Icon,
                        anchor,
                        action,
                        badge,
                      })
                    }
                    title={label}
                  >
                    <div style={{ position: "relative" }}>
                      <Icon className="footer-icon" aria-hidden />
                      {badge ? (
                        <span
                          style={{
                            position: "absolute",
                            top: -4,
                            right: -8,
                            minWidth: 16,
                            height: 16,
                            padding: "0 4px",
                            borderRadius: 999,
                            background: "#ef4444",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {badge > 9 ? "9+" : badge}
                        </span>
                      ) : null}
                    </div>
                    <span className="footer-label">{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </>
  );
};

const styles = {
  nav: (elevation) => ({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
    background: "#fff",
    borderTop: "1px solid rgba(229,231,235,0.9)",
    boxShadow: elevation ? "0 -8px 24px -16px rgba(0,0,0,0.18)" : "none",
    backdropFilter: "saturate(120%) blur(10px)",
    WebkitBackdropFilter: "saturate(120%) blur(10px)",
  }),
};

const css = `
  .footer-wrap {
    position: relative;
    max-width: 1100px;
    margin: 0 auto;
    padding: 10px 16px 18px;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
  }

  .footer-grid {
    display: grid;
    gap: 4px;
    align-items: center;
  }
  .footer-grid > li { list-style: none; }

  .footer-btn {
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    border: none;
    border-radius: 12px;
    padding: 10px 6px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: transform .15s ease, background .15s ease, box-shadow .15s ease;
    color: #4b5563;
  }

  @media (hover:hover) and (pointer:fine){
    .footer-btn:hover { background: #f3f4f6; }
  }

  .footer-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.35);
    background: #f3f4f6;
  }

  .footer-btn--active {
    color: #0a66c2;
    background: #eef6ff;
    border: 1px solid rgba(10,102,194,0.25);
  }

  .footer-label {
    font-size: 11px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: .2px;
  }

  .footer-icon { width: 20px; height: 20px; }

  @media (prefers-reduced-motion: reduce) {
    .footer-btn { transition: none; }
  }
  @supports not (backdrop-filter: blur(10px)) {
    nav[role="navigation"] { background: #fff; }
  }
`;

export default React.memo(AppFooter);
